import { useEffect, useState } from "react";

export type AnnualReport = {
  year: number;
  activeSubmissionDays: number;
  submissionVersions: number;
  approvedMilestones: number;
  teacherFeedbacks: number | null;
  completedShots: number | null;
  acceptedMinutes: number | null;
  completedDramas: number | null;
  completedStageProjects: number | null;
  finalized: boolean;
};

function paintReport(report: AnnualReport, name: string, url: string) {
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1350;
  const context = canvas.getContext("2d");
  if (!context) return "";
  const { width, height } = canvas;
  const backdrop = context.createLinearGradient(0, 0, width, height);
  backdrop.addColorStop(0, "#142b40");
  backdrop.addColorStop(0.45, "#101d2b");
  backdrop.addColorStop(1, "#080d13");
  context.fillStyle = backdrop;
  context.fillRect(0, 0, width, height);
  const glow = context.createRadialGradient(810, 160, 30, 810, 160, 620);
  glow.addColorStop(0, "rgba(41,167,255,.28)");
  glow.addColorStop(1, "rgba(41,167,255,0)");
  context.fillStyle = glow;
  context.fillRect(0, 0, width, height);

  const text = (value: string, x: number, y: number, size: number, color: string, weight = 500) => {
    context.font = `${weight} ${size}px "Noto Sans CJK SC", "Microsoft YaHei", sans-serif`;
    context.fillStyle = color;
    context.fillText(value, x, y);
  };
  const panel = (x: number, y: number, w: number, h: number) => {
    context.beginPath();
    context.roundRect(x, y, w, h, 24);
    context.fillStyle = "rgba(18,35,51,.78)";
    context.fill();
    context.strokeStyle = "rgba(115,190,240,.2)";
    context.lineWidth = 2;
    context.stroke();
  };
  const mainMetric = (x: number, y: number, value: string, label: string, unit: string) => {
    panel(x, y, 440, 205);
    text(value, x + 32, y + 108, 74, "#eaf7ff", 750);
    text(unit, x + 32, y + 151, 23, "#70c4fc", 600);
    text(label, x + 32, y + 183, 22, "#a9becc");
  };
  text("✦  繁星短剧训练营  /  创作者空间", 78, 88, 28, "#a8dafe", 700);
  text(String(report.year), 78, 200, 94, "#6ac3ff", 800);
  text("短剧创作报告", 78, 290, 66, "#f2f9ff", 750);
  text(`${name} 的年度记录`, 82, 358, 31, "#bdcbd8", 550);
  text(report.finalized ? "完整年度" : "持续更新中", 795, 352, 23, "#79c8f9", 650);
  context.fillStyle = "rgba(129,191,230,.28)";
  context.fillRect(78, 403, 924, 2);

  mainMetric(78, 455, String(report.activeSubmissionDays), "按版本提交日期统计", "有提交的创作日");
  mainMetric(562, 455, String(report.submissionVersions), "项目持续迭代的记录", "提交版本");
  mainMetric(78, 685, String(report.approvedMilestones), "教师审核通过的任务", "通过里程碑");
  mainMetric(562, 685, report.teacherFeedbacks === null ? "—" : String(report.teacherFeedbacks), report.teacherFeedbacks === null ? "未授权公开" : "教师反馈记录", "教师反馈");

  panel(78, 944, 924, 206);
  text("更多制作成果", 109, 994, 27, "#e6f4ff", 700);
  const later = [
    ["镜头数", report.completedShots],
    ["成片分钟", report.acceptedMinutes],
    ["完整短剧", report.completedDramas],
    ["阶段项目", report.completedStageProjects],
  ] as const;
  later.forEach(([label, value], index) => {
    const x = 109 + index * 223;
    text(value === null ? "—" : String(value), x, 1066, 37, "#f2f7fa", 700);
    text(label, x, 1107, 21, "#aabccb");
  });
  text("未接入验收记录的指标不估算、不补填", 78, 1212, 22, "#8fa9bb");
  text("DUANJU.SCHOOL", 78, 1290, 25, "#72c7ff", 700);
  const address = new URL(url);
  text(`${address.host}/u/${address.pathname.split("/").pop() || ""}`, 360, 1290, 21, "#90a8ba");
  return canvas.toDataURL("image/png");
}

export default function AnnualReportShare({ report, name, onClose }: { report: AnnualReport; name: string; onClose: () => void }) {
  const [image, setImage] = useState("");
  useEffect(() => {
    setImage(paintReport(report, name, window.location.href));
  }, [report, name]);
  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);
  return <div className="creator-report-overlay" role="presentation" onMouseDown={onClose}>
    <div className="creator-report-dialog" role="dialog" aria-modal="true" aria-label={`${report.year} 年创作报告分享卡`} onMouseDown={(event) => event.stopPropagation()}>
      <div className="creator-report-dialog-head"><div><span>年度创作报告</span><h2>把这一年的创作，分享出去。</h2></div><button type="button" aria-label="关闭分享卡" onClick={onClose}>×</button></div>
      {image ? <img className="creator-report-preview" src={image} alt={`${name} 的 ${report.year} 年短剧创作报告分享卡`} /> : <p>正在生成分享卡…</p>}
      <div className="creator-report-actions"><p>根据已公开的真实项目记录生成，未接入的数据不会估算。</p>{image ? <a href={image} download={`${name}-${report.year}-短剧创作报告.png`}>保存图片 ↓</a> : null}</div>
    </div>
  </div>;
}
